import type {Ficha, FichaDeEmpresa} from '@cnc/contracts';
import {useEffect, useState, type ReactNode} from 'react';
import {useCrearFicha, useEditarFicha, useFichasDeEmpresas} from '../api/cumplimiento';
import {tr, type Texto} from '../i18n/idioma';
import {fechaHora} from '../utilidades/formato';
import {AYUDA_FICHA as AF} from './ayuda-cumplimiento';
import {NotaDeAyuda} from './nota-de-ayuda';
import {usePermisos} from './sesion-contexto';

type Persona = NonNullable<FichaDeEmpresa['representanteLegal']>;
type Accionista = NonNullable<FichaDeEmpresa['accionistas']>[number];

const campo = 'mt-1 w-full rounded-md border border-linea bg-white px-2 py-1.5 text-sm';

// Cumplimiento → Fichas de empresas: los datos de cada empresa del grupo que
// piden los formularios de terceros (razon social, identificacion, direccion,
// representante legal, banco, accionistas...). Se guardan una vez y despues
// sirven para llenar los formularios. Cualquier rol las ve; solo quien tiene
// permiso de edicion las crea y cambia (Daniel, 12/10/2026).
export function FichasDeEmpresas() {
  const permisos = usePermisos();
  const fichas = useFichasDeEmpresas(true);
  const crear = useCrearFicha();
  const [elegida, setElegida] = useState<number | null>(null);
  const [nueva, setNueva] = useState('');
  const lista = fichas.data?.fichas ?? [];
  const actual = lista.find(f => f.id === elegida) ?? lista[0] ?? null;
  return (
    <div className="grid gap-4 lg:grid-cols-[16rem_1fr]" data-prueba="cumplimiento-fichas">
      <aside className="space-y-3">
        <p className="text-sm text-apagado">
          {tr(
            'Los datos de cada empresa del grupo que piden los formularios de terceros. Se llenan una vez y quedan listos para usar.',
            'The details of each group company that third-party forms ask for. Fill them once and they are ready to use.',
          )}
        </p>
        <ul className="space-y-1">
          {lista.map(f => (
            <li key={f.id}>
              <button
                type="button"
                onClick={() => setElegida(f.id)}
                className={`w-full rounded-md px-3 py-1.5 text-left text-sm ${f.id === actual?.id ? 'bg-verde-claro font-medium text-verde-oscuro' : 'hover:bg-papel'}`}
              >
                {f.nombre}
              </button>
            </li>
          ))}
        </ul>
        {permisos.editar && (
          <form
            className="flex gap-2"
            onSubmit={e => {
              e.preventDefault();
              crear.mutate(
                {nombre: nueva.trim()},
                {
                  onSuccess: r => {
                    setNueva('');
                    setElegida(r.fichas.find(f => f.nombre.toLowerCase() === nueva.trim().toLowerCase())?.id ?? null);
                  },
                },
              );
            }}
          >
            <input
              value={nueva}
              onChange={e => setNueva(e.target.value)}
              placeholder={tr('Empresa nueva', 'New company')}
              aria-label={tr('Nombre de la empresa nueva', 'New company name')}
              className="min-w-0 flex-1 rounded-md border border-linea bg-white px-2 py-1 text-sm"
            />
            <button
              type="submit"
              disabled={!nueva.trim() || crear.isPending}
              className="rounded-md bg-verde px-3 py-1 text-sm text-white hover:bg-verde-oscuro disabled:opacity-50"
            >
              {tr('Agregar', 'Add')}
            </button>
          </form>
        )}
        {crear.isError && <p className="text-xs text-alerta">{crear.error.message}</p>}
      </aside>
      {fichas.isError && <p className="text-sm text-alerta">{fichas.error.message}</p>}
      {actual ? (
        <EditorDeFicha key={actual.id} ficha={actual} soloLectura={!permisos.editar} />
      ) : (
        <p className="text-sm text-apagado">
          {fichas.isPending
            ? tr('Cargando…', 'Loading…')
            : tr(
                'Todavía no hay fichas. Agrega la primera empresa.',
                'There are no profiles yet. Add the first company.',
              )}
        </p>
      )}
    </div>
  );
}

function EditorDeFicha({ficha, soloLectura}: {ficha: Ficha; soloLectura: boolean}) {
  const editar = useEditarFicha();
  const [datos, setDatos] = useState<FichaDeEmpresa>(ficha.datos);
  const [guardado, setGuardado] = useState(false);
  useEffect(() => setGuardado(false), [datos]);
  const texto = (clave: keyof FichaDeEmpresa, etiqueta: string, ancho = '') => (
    <label className={`block text-xs text-apagado ${ancho}`}>
      {etiqueta}
      {clave in AF && <NotaDeAyuda texto={AF[clave as keyof typeof AF]} dentroDeEtiqueta />}
      <input
        value={(datos[clave] as string | undefined) ?? ''}
        onChange={e => setDatos({...datos, [clave]: e.target.value})}
        className={campo}
      />
    </label>
  );
  const persona = (
    clave: 'representanteLegal' | 'representanteSuplente' | 'oficialDeCumplimiento' | 'contador' | 'revisorFiscal',
    titulo: string,
    conTarjeta = false,
  ) => {
    const valor = (datos[clave] ?? {}) as Persona & {tarjetaProfesional?: string};
    const cambiar = (k: string, v: string) => setDatos({...datos, [clave]: {...valor, [k]: v}});
    const dato = (k: string, etiqueta: string, ayuda?: Texto) => (
      <label className="block text-xs text-apagado">
        {etiqueta}
        {ayuda && <NotaDeAyuda texto={ayuda} dentroDeEtiqueta />}
        <input
          value={(valor as Record<string, string | undefined>)[k] ?? ''}
          onChange={e => cambiar(k, e.target.value)}
          className={campo}
        />
      </label>
    );
    return (
      <Seccion titulo={titulo} ayuda={AF.persona}>
        {dato('nombre', tr('Nombre completo', 'Full name'))}
        {dato('tipoDeDocumento', tr('Tipo de documento', 'ID type'), AF.tipoDeDocumento)}
        {dato('numeroDeDocumento', tr('Número de documento', 'ID number'))}
        {dato('nacionalidad', tr('Nacionalidad', 'Nationality'))}
        {dato('cargo', tr('Cargo', 'Position'))}
        {dato('correo', tr('Correo', 'Email'))}
        {dato('telefono', tr('Teléfono', 'Phone'))}
        {conTarjeta &&
          dato('tarjetaProfesional', tr('Tarjeta profesional', 'Professional license'), AF.tarjetaProfesional)}
      </Seccion>
    );
  };
  const banco = datos.banco ?? {};
  const datoDeBanco = (k: keyof typeof banco, etiqueta: string, ayuda: Texto) => (
    <label className="block text-xs text-apagado">
      {etiqueta}
      <NotaDeAyuda texto={ayuda} dentroDeEtiqueta />
      <input
        value={banco[k] ?? ''}
        onChange={e => setDatos({...datos, banco: {...banco, [k]: e.target.value}})}
        className={campo}
      />
    </label>
  );
  const accionistas = datos.accionistas ?? [];
  const cambiarAccionista = (i: number, k: keyof Accionista, v: string) =>
    setDatos({...datos, accionistas: accionistas.map((a, j) => (j === i ? {...a, [k]: v} : a))});
  return (
    <form
      className="space-y-4"
      onSubmit={e => {
        e.preventDefault();
        editar.mutate({id: ficha.id, edicion: {version: ficha.version, datos}}, {onSuccess: () => setGuardado(true)});
      }}
      data-prueba="editor-de-ficha"
    >
      {/* El encabezado queda fuera del fieldset: su nota (i) se abre también en solo lectura. */}
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex items-baseline gap-1">
          <h2 className="text-lg font-semibold text-verde-oscuro">{ficha.nombre}</h2>
          <NotaDeAyuda titulo={ficha.nombre} texto={AF.general} />
        </div>
        <span className="text-xs text-apagado">
          {tr('Actualizada el', 'Updated on')} {fechaHora(ficha.actualizadaEl)}
          {ficha.actualizadaPor && ` · ${ficha.actualizadaPor}`}
        </span>
      </div>
      {/* Consulta la ve en solo lectura: el fieldset deshabilitado bloquea todos los campos y botones. */}
      <fieldset disabled={soloLectura} className="contents">
        <Seccion titulo={tr('Empresa', 'Company')}>
          {texto('razonSocial', tr('Razón social', 'Legal name'), 'sm:col-span-2')}
          {texto('nombreComercial', tr('Nombre comercial', 'Trade name'))}
          {texto('tipoDeIdentificacion', tr('Tipo de identificación (NIT, Tax ID…)', 'ID type (NIT, Tax ID…)'))}
          {texto('numeroDeIdentificacion', tr('Número de identificación', 'ID number'))}
          {texto('fechaDeConstitucion', tr('Fecha de constitución', 'Incorporation date'))}
          {texto('actividadEconomica', tr('Actividad económica', 'Business activity'), 'sm:col-span-2')}
          {texto('codigoCiiu', tr('Código CIIU', 'CIIU code'))}
        </Seccion>
        <Seccion titulo={tr('Contacto y dirección', 'Contact and address')}>
          {texto('direccion', tr('Dirección', 'Address'), 'sm:col-span-2')}
          {texto('ciudad', tr('Ciudad', 'City'))}
          {texto('pais', tr('País', 'Country'))}
          {texto('codigoPostal', tr('Código postal', 'Postal code'))}
          {texto('telefono', tr('Teléfono', 'Phone'))}
          {texto('correo', tr('Correo', 'Email'))}
          {texto('sitioWeb', tr('Sitio web', 'Website'))}
        </Seccion>
        {persona('representanteLegal', tr('Representante legal', 'Legal representative'))}
        {persona('representanteSuplente', tr('Representante legal suplente', 'Alternate legal representative'))}
        {persona('oficialDeCumplimiento', tr('Oficial de cumplimiento', 'Compliance officer'))}
        {persona('contador', tr('Contador', 'Accountant'), true)}
        {persona('revisorFiscal', tr('Revisor fiscal', 'Statutory auditor'), true)}
        <Seccion titulo={tr('Cuenta bancaria', 'Bank account')}>
          {datoDeBanco('nombre', tr('Banco', 'Bank'), AF.banco)}
          {datoDeBanco('pais', tr('País', 'Country'), AF.paisBanco)}
          {datoDeBanco('tipoDeCuenta', tr('Tipo de cuenta', 'Account type'), AF.tipoDeCuenta)}
          {datoDeBanco('numeroDeCuenta', tr('Número de cuenta', 'Account number'), AF.numeroDeCuenta)}
          {datoDeBanco('swift', 'SWIFT', AF.swift)}
          {datoDeBanco('titular', tr('Titular', 'Account holder'), AF.titular)}
          {datoDeBanco('direccion', tr('Dirección del banco', 'Bank address'), AF.direccionBanco)}
          {datoDeBanco('bancoCorresponsal', tr('Banco corresponsal', 'Correspondent bank'), AF.bancoCorresponsal)}
          {datoDeBanco(
            'cuentaCorresponsal',
            tr('Cuenta del corresponsal', 'Correspondent account'),
            AF.cuentaCorresponsal,
          )}
          {datoDeBanco('swiftCorresponsal', tr('SWIFT del corresponsal', 'Correspondent SWIFT'), AF.swiftCorresponsal)}
        </Seccion>
        <fieldset className="rounded-lg border border-linea bg-white p-3">
          <legend className="px-1 text-sm font-semibold tracking-wide text-apagado uppercase">
            {tr('Accionistas y beneficiarios finales', 'Shareholders and beneficial owners')}
            <NotaDeAyuda
              titulo={tr('Accionistas y beneficiarios finales', 'Shareholders and beneficial owners')}
              texto={AF.accionistas}
            />
          </legend>
          <div className="space-y-2">
            {accionistas.map((a, i) => (
              <div key={i} className="grid gap-2 sm:grid-cols-[2fr_1fr_1fr_6rem_auto]">
                {(['nombre', 'identificacion', 'pais', 'porcentaje'] as const).map(k => (
                  <input
                    key={k}
                    value={a[k] ?? ''}
                    onChange={e => cambiarAccionista(i, k, e.target.value)}
                    placeholder={
                      {
                        nombre: tr('Nombre', 'Name'),
                        identificacion: tr('Identificación', 'ID'),
                        pais: tr('País', 'Country'),
                        porcentaje: '%',
                      }[k]
                    }
                    aria-label={tr(`Accionista ${i + 1}: ${k}`, `Shareholder ${i + 1}: ${k}`)}
                    className="rounded-md border border-linea bg-white px-2 py-1 text-sm"
                  />
                ))}
                <button
                  type="button"
                  onClick={() => setDatos({...datos, accionistas: accionistas.filter((_, j) => j !== i)})}
                  aria-label={tr(`Quitar accionista ${i + 1}`, `Remove shareholder ${i + 1}`)}
                  className="rounded-md border border-linea px-2 text-sm hover:bg-papel"
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setDatos({...datos, accionistas: [...accionistas, {}]})}
              className="text-sm text-verde hover:underline"
            >
              ✚ {tr('Agregar accionista', 'Add shareholder')}
            </button>
          </div>
        </fieldset>
        <label className="block text-xs text-apagado">
          {tr('Notas', 'Notes')}
          <NotaDeAyuda texto={AF.notas} dentroDeEtiqueta />
          <textarea
            value={datos.notas ?? ''}
            onChange={e => setDatos({...datos, notas: e.target.value})}
            rows={3}
            className={campo}
          />
        </label>
        {!soloLectura && (
          <div className="sticky bottom-0 flex items-center gap-3 border-t border-linea bg-papel py-2">
            <button
              type="submit"
              disabled={editar.isPending}
              className="rounded-md bg-verde px-4 py-1.5 text-sm text-white hover:bg-verde-oscuro disabled:opacity-50"
            >
              {editar.isPending ? tr('Guardando…', 'Saving…') : tr('Guardar ficha', 'Save profile')}
            </button>
            {guardado && <span className="text-sm text-verde-oscuro">{tr('Guardada. ✓', 'Saved. ✓')}</span>}
            {editar.isError && <span className="text-sm text-alerta">{editar.error.message}</span>}
          </div>
        )}
      </fieldset>
    </form>
  );
}

function Seccion({titulo, children, ayuda}: {titulo: string; children: ReactNode; ayuda?: Texto}) {
  return (
    <fieldset className="rounded-lg border border-linea bg-white p-3">
      <legend className="px-1 text-sm font-semibold tracking-wide text-apagado uppercase">
        {titulo}
        {ayuda && <NotaDeAyuda titulo={titulo} texto={ayuda} />}
      </legend>
      <div className="grid gap-3 sm:grid-cols-3">{children}</div>
    </fieldset>
  );
}
